import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  LogOut,
  ArrowLeft,
  Plus,
  Pencil,
  Trash2,
  Loader2,
  Key,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatCurrency = (value) =>
  new Intl.NumberFormat("pt-PT", { style: "currency", currency: "EUR" }).format(value || 0);

const CATEGORIES = [
  { value: "productivity", label: "Produtividade" },
  { value: "development", label: "Desenvolvimento" },
  { value: "design", label: "Design" },
  { value: "security", label: "Segurança" },
  { value: "other", label: "Outro" },
];

const defaultForm = {
  name: "",
  category: "productivity",
  monthly_cost: 0,
  annual_cost: 0,
  description: "",
  active: true,
};

const AdminLicenses = () => {
  const navigate = useNavigate();
  const { user, logout, getAuthHeaders } = useAuth();
  const [loading, setLoading] = useState(true);
  const [licenses, setLicenses] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingLicense, setEditingLicense] = useState(null);
  const [formData, setFormData] = useState(defaultForm);

  useEffect(() => {
    loadLicenses();
  }, []);

  const loadLicenses = async () => {
    try {
      const response = await axios.get(`${API}/licenses?active=true`, { headers: getAuthHeaders() });
      setLicenses(response.data);
    } catch (e) {
      toast.error("Erro ao carregar licenças");
    } finally {
      setLoading(false);
    }
  };

  const openNewDialog = () => {
    setEditingLicense(null);
    setFormData(defaultForm);
    setIsDialogOpen(true);
  };

  const openEditDialog = (lic) => {
    setEditingLicense(lic);
    setFormData({
      name: lic.name,
      category: lic.category,
      monthly_cost: lic.monthly_cost,
      annual_cost: lic.annual_cost || 0,
      description: lic.description || "",
      active: lic.active,
    });
    setIsDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        monthly_cost: parseFloat(formData.monthly_cost) || 0,
        annual_cost: parseFloat(formData.annual_cost) || 0,
      };
      if (editingLicense) {
        await axios.put(`${API}/licenses/${editingLicense.id}`, payload, { headers: getAuthHeaders() });
        toast.success("Licença atualizada!");
      } else {
        await axios.post(`${API}/licenses`, payload, { headers: getAuthHeaders() });
        toast.success("Licença criada!");
      }
      setIsDialogOpen(false);
      loadLicenses();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao guardar licença");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Eliminar esta licença?")) return;
    try {
      await axios.delete(`${API}/licenses/${id}`, { headers: getAuthHeaders() });
      toast.success("Licença eliminada");
      loadLicenses();
    } catch (err) {
      toast.error("Erro ao eliminar");
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  const getCategoryLabel = (c) => CATEGORIES.find((cat) => cat.value === c)?.label || c;

  return (
    <div className="min-h-screen bg-background" data-testid="admin-licenses-page">
      <header className="sticky top-0 z-50 glass border-b border-slate-200/50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="bg-white rounded-lg px-3 py-1.5">
              <img src="/evolutt_logo_white.png" alt="Evolutt" className="h-8" />
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Licenças</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-muted-foreground">{user?.name}</span>
            <Button variant="ghost" size="sm" onClick={handleLogout} data-testid="logout-btn">
              <LogOut className="w-4 h-4 mr-2" />
              Sair
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Link to="/admin">
              <Button variant="outline" size="sm" data-testid="back-btn">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Voltar
              </Button>
            </Link>
            <div>
              <h1 className="font-manrope font-extrabold text-2xl text-foreground">Licenças</h1>
              <p className="text-muted-foreground">Software e licenças mensais</p>
            </div>
          </div>

          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button
                className="bg-brand-600 text-white hover:bg-brand-700 font-bold"
                onClick={openNewDialog}
                data-testid="add-license-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Nova Licença
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle className="font-manrope font-bold text-xl">
                  {editingLicense ? "Editar Licença" : "Nova Licença"}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label>Nome</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Ex: Microsoft 365 E3"
                    data-testid="name-input"
                    required
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Categoria</Label>
                    <Select
                      value={formData.category}
                      onValueChange={(v) => setFormData({ ...formData, category: v })}
                    >
                      <SelectTrigger data-testid="category-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {CATEGORIES.map((c) => (
                          <SelectItem key={c.value} value={c.value}>
                            {c.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Custo Mensal (€)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={formData.monthly_cost}
                      onChange={(e) => {
                        const m = e.target.value;
                        setFormData({ ...formData, monthly_cost: m, annual_cost: parseFloat(((parseFloat(m) || 0) * 12).toFixed(2)) });
                      }}
                      className="font-mono"
                      data-testid="monthly-cost-input"
                      required
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Custo Anual (€) — calcula mensal automaticamente</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.annual_cost}
                    onChange={(e) => {
                      const a = e.target.value;
                      setFormData({ ...formData, annual_cost: a, monthly_cost: parseFloat(((parseFloat(a) || 0) / 12).toFixed(2)) });
                    }}
                    className="font-mono"
                    data-testid="annual-cost-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Descrição</Label>
                  <Input
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Descrição opcional"
                    data-testid="description-input"
                  />
                </div>
                <div className="flex items-center justify-between pt-2">
                  <Label>Ativa</Label>
                  <Switch
                    checked={formData.active}
                    onCheckedChange={(c) => setFormData({ ...formData, active: c })}
                    data-testid="active-switch"
                  />
                </div>
                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                    Cancelar
                  </Button>
                  <Button
                    type="submit"
                    className="bg-brand-600 text-white hover:bg-brand-700"
                    data-testid="save-btn"
                  >
                    {editingLicense ? "Guardar" : "Criar"}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <Card className="bg-white border border-border/60 shadow-sm">
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
              </div>
            ) : licenses.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-64 text-center">
                <Key className="w-12 h-12 text-muted-foreground mb-4" />
                <p className="text-muted-foreground">Nenhuma licença configurada</p>
                <p className="text-sm text-muted-foreground">Clique em "Nova Licença" para adicionar</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/50">
                    <TableHead className="font-semibold">Nome</TableHead>
                    <TableHead className="font-semibold">Categoria</TableHead>
                    <TableHead className="font-semibold">Mensal</TableHead>
                    <TableHead className="font-semibold">Anual</TableHead>
                    <TableHead className="font-semibold text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {licenses.map((lic) => (
                    <TableRow key={lic.id} data-testid={`license-row-${lic.id}`}>
                      <TableCell>
                        <div>
                          <p className="font-medium">{lic.name}</p>
                          {lic.description && (
                            <p className="text-xs text-muted-foreground">{lic.description}</p>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="secondary">{getCategoryLabel(lic.category)}</Badge>
                      </TableCell>
                      <TableCell className="font-mono font-medium text-brand-700">
                        {formatCurrency(lic.monthly_cost)}
                      </TableCell>
                      <TableCell className="font-mono text-muted-foreground">
                        {formatCurrency(lic.annual_cost || lic.monthly_cost * 12)}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => openEditDialog(lic)}
                            data-testid={`edit-btn-${lic.id}`}
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-destructive"
                            onClick={() => handleDelete(lic.id)}
                            data-testid={`delete-btn-${lic.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default AdminLicenses;
